import type { ComponentPropsWithoutRef } from "react";
import { forwardRef } from "react";
import type { VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { buttonVariants } from "@/components/ui/buttonVariants";
import { cn } from "@/lib/cn";

// Re-export the shared types so existing imports from "@/components/ui/Button"
// keep working (types are erased — no Fast Refresh impact).
export type { ButtonVariant, ButtonSize } from "@/components/ui/buttonVariants";

interface Props
  extends ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // BUG-387: default to `type="button"`, because a <button> with no type
      // defaults to `submit` in HTML. That is how «Отмена» in the order-detail
      // modal PATCHed the order instead of discarding the edit — the handler
      // returned early, but the native submit had already fired.
      //
      // `type` is placed BEFORE `{...props}` deliberately: after it, this
      // default would OVERRIDE all 83 explicit `type="submit"` call sites and
      // break every form in the app. The spread must win.
      //
      // Audited before changing (2026-08-24): 83 explicit `type="submit"`, and
      // exactly 2 `<Button>` in a file containing a <form> with neither `type`
      // nor `onClick` — both `disabled` placeholders in AdminBillingPage that
      // cannot submit anything. So nothing relies on the implicit default.
      //
      // NOT applied when `asChild` is set: `Slot` renders the child element,
      // which may be an <a> or a <div>, and `type` is meaningless — or wrong —
      // on those.
      {...(asChild ? {} : { type: "button" as const })}
      {...props}
    />
  );
});
